import { format } from "date-fns";
import { useCallback, useState } from "react";
import StatisticApi from "../../api/statisticApi";
import s from "./MetaData.module.css";

interface DateRange {
  from: Date | null;
  to: Date | null;
}

type DownloadType = "client" | "session";

interface DownloadStatus {
  type: DownloadType;
  fileName: string;
  at: Date;
}

const toDateFromInput = (value: string): Date | null => {
  if (!value) return null;

  return new Date(value);
};

const MetaData = () => {
  const token = localStorage.getItem("token");
  const [firstVisitDate, setFirstVisitDate] = useState<DateRange>({
    from: null,
    to: null,
  });
  const [registerDate, setRegisterDate] = useState<DateRange>({
    from: null,
    to: null,
  });
  const [sessionDate, setSessionDate] = useState<DateRange>({
    from: null,
    to: null,
  });

  const [loadingClient, setLoadingClient] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<DownloadStatus | null>(null);

  const hasInvalidRange = (range: DateRange) => {
    if (!range.from || !range.to) return false;

    return range.from > range.to;
  };

  const downloadCsvFile = useCallback(
    (csvContent: string, fileName: string) => {
      const bom = "\uFEFF";
      const blob = new Blob([bom + csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    },
    [],
  );

  const handleDownloadClientMetadata = async () => {
    if (!token) {
      setError("Authorization token is missing");
      return;
    }

    if (hasInvalidRange(firstVisitDate) || hasInvalidRange(registerDate)) {
      setError("Invalid date range: start date must be earlier than end date");
      return;
    }

    setLoadingClient(true);
    setError(null);
    setStatus(null);

    const payload: {
      firstVisitDate?: { from: Date; to: Date };
      registerDate?: { from: Date; to: Date };
    } = {};

    if (firstVisitDate.from && firstVisitDate.to) {
      payload.firstVisitDate = {
        from: firstVisitDate.from,
        to: firstVisitDate.to,
      };
    }

    if (registerDate.from && registerDate.to) {
      payload.registerDate = {
        from: registerDate.from,
        to: registerDate.to,
      };
    }

    try {
      const csvContent = await StatisticApi.downloadClientMetadataTable(
        token,
        payload,
      );

      if (!csvContent.trim()) {
        setError("No data to download");
        return;
      }

      const fileName = `client_metadata_${format(
        new Date(),
        "yyyy-MM-dd_HH-mm-ss",
      )}.csv`;

      downloadCsvFile(csvContent, fileName);
      setStatus({ type: "client", fileName, at: new Date() });
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || "Error downloading file");
      } else {
        setError("Error downloading file");
      }
    } finally {
      setLoadingClient(false);
    }
  };

  const handleDownloadSessionMetadata = async () => {
    if (!token) {
      setError("Authorization token is missing");
      return;
    }

    if (hasInvalidRange(sessionDate)) {
      setError(
        "Invalid session date range: start date must be earlier than end date",
      );
      return;
    }

    setLoadingSession(true);
    setError(null);
    setStatus(null);

    const payload: {
      sessionDate: { from?: Date; to?: Date };
    } = { sessionDate: {} };

    if (sessionDate.from && sessionDate.to) {
      payload.sessionDate = {
        from: sessionDate.from,
        to: sessionDate.to,
      };
    }

    try {
      const csvContent = await StatisticApi.downloadSessionMetadataTable(
        token,
        payload,
      );

      if (!csvContent.trim()) {
        setError("No session data to download");
        return;
      }

      const fileName = `session_metadata_${format(
        new Date(),
        "yyyy-MM-dd_HH-mm-ss",
      )}.csv`;

      downloadCsvFile(csvContent, fileName);
      setStatus({ type: "session", fileName, at: new Date() });
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || "Error downloading file");
      } else {
        setError("Error downloading file");
      }
    } finally {
      setLoadingSession(false);
    }
  };

  const formatDateForInput = (date: Date | null) =>
    date ? format(date, "yyyy-MM-dd") : "";

  const hasActiveFilters =
    firstVisitDate.from ||
    firstVisitDate.to ||
    registerDate.from ||
    registerDate.to ||
    sessionDate.from ||
    sessionDate.to;

  const handleResetFilters = useCallback(() => {
    setFirstVisitDate({ from: null, to: null });
    setRegisterDate({ from: null, to: null });
    setSessionDate({ from: null, to: null });
    setError(null);
    setStatus(null);
  }, []);

  return (
    <div className={s.container}>
      <div className={s.header}>
        <h2 className={s.title}>Metadata export center</h2>
        <p className={s.subtitle}>
          Configure date ranges and export CSV files for client and session
          analytics.
        </p>
      </div>

      {status && (
        <div className={s.success}>
          <span className={s.successTitle}>CSV downloaded successfully.</span>
          <span className={s.successText}>
            {status.type === "client" ? "Client metadata" : "Session metadata"}:{" "}
            {status.fileName}
          </span>
          <span className={s.successText}>
            {format(status.at, "dd.MM.yyyy HH:mm:ss")}
          </span>
        </div>
      )}

      <section className={s.card}>
        <div className={s.cardHeader}>
          <h3 className={s.cardTitle}>Client metadata</h3>
          <p className={s.cardDescription}>
            Export with optional filters by first visit and registration date.
          </p>
        </div>

        <div className={s.filters}>
          <div className={s.filterGroup}>
            <label className={s.label}>Date of first visit</label>
            <div className={s.dateInputs}>
              <input
                type="date"
                value={formatDateForInput(firstVisitDate.from)}
                onChange={(e) =>
                  setFirstVisitDate({
                    ...firstVisitDate,
                    from: toDateFromInput(e.target.value),
                  })
                }
                className={s.dateInput}
              />
              <span className={s.dateSeparator}>to</span>
              <input
                type="date"
                value={formatDateForInput(firstVisitDate.to)}
                onChange={(e) =>
                  setFirstVisitDate({
                    ...firstVisitDate,
                    to: toDateFromInput(e.target.value),
                  })
                }
                className={s.dateInput}
              />
            </div>
          </div>

          <div className={s.filterGroup}>
            <label className={s.label}>Date of registration</label>
            <div className={s.dateInputs}>
              <input
                type="date"
                value={formatDateForInput(registerDate.from)}
                onChange={(e) =>
                  setRegisterDate({
                    ...registerDate,
                    from: toDateFromInput(e.target.value),
                  })
                }
                className={s.dateInput}
              />
              <span className={s.dateSeparator}>to</span>
              <input
                type="date"
                value={formatDateForInput(registerDate.to)}
                onChange={(e) =>
                  setRegisterDate({
                    ...registerDate,
                    to: toDateFromInput(e.target.value),
                  })
                }
                className={s.dateInput}
              />
            </div>
          </div>
        </div>

        <div className={s.actions}>
          <button
            onClick={handleDownloadClientMetadata}
            disabled={loadingClient || loadingSession}
            className={s.downloadButton}
          >
            {loadingClient ? "Preparing CSV..." : "Download client CSV"}
          </button>

          <button
            onClick={handleResetFilters}
            disabled={!hasActiveFilters || loadingClient || loadingSession}
            className={s.resetButton}
          >
            Reset all filters
          </button>
        </div>
      </section>

      <section className={s.card}>
        <div className={s.cardHeader}>
          <h3 className={s.cardTitle}>Session metadata</h3>
          <p className={s.cardDescription}>
            Export session-level metadata with an optional session date range.
          </p>
        </div>

        <div className={s.filters}>
          <div className={s.filterGroup}>
            <label className={s.label}>Session date</label>
            <div className={s.dateInputs}>
              <input
                type="date"
                value={formatDateForInput(sessionDate.from)}
                onChange={(e) =>
                  setSessionDate({
                    ...sessionDate,
                    from: toDateFromInput(e.target.value),
                  })
                }
                className={s.dateInput}
              />
              <span className={s.dateSeparator}>to</span>
              <input
                type="date"
                value={formatDateForInput(sessionDate.to)}
                onChange={(e) =>
                  setSessionDate({
                    ...sessionDate,
                    to: toDateFromInput(e.target.value),
                  })
                }
                className={s.dateInput}
              />
            </div>
          </div>
        </div>

        <div className={s.actions}>
          <button
            onClick={handleDownloadSessionMetadata}
            disabled={loadingSession || loadingClient}
            className={s.downloadButton}
          >
            {loadingSession ? "Preparing CSV..." : "Download session CSV"}
          </button>

          <button
            onClick={() => {
              setSessionDate({ from: null, to: null });
              setError(null);
              setStatus(null);
            }}
            disabled={
              (!sessionDate.from && !sessionDate.to) ||
              loadingSession ||
              loadingClient
            }
            className={s.resetButton}
          >
            Reset session filter
          </button>
        </div>
      </section>

      {(hasInvalidRange(firstVisitDate) ||
        hasInvalidRange(registerDate) ||
        hasInvalidRange(sessionDate)) && (
        <div className={s.warning}>
          Start date must be earlier than or equal to end date.
        </div>
      )}

      <div className={s.footerHint}>
        Leave filters empty to export the full dataset.
      </div>

      {error && <div className={s.error}>{error}</div>}
    </div>
  );
};

export default MetaData;
